import { Component, ElementRef, OnDestroy, OnInit, computed, effect, inject, signal, viewChild } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { LocationCardComponent } from './location-card/location-card.component';
import { WeatherApiService } from '@app/modules/core/weather/weather-api.service';
import { WeatherCondition } from '@app/modules/core/weather/weather.model';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { WidgetLocation } from '@app/features/operating-picture/map-wall-display/models';
import { MapDrawingService } from '@app/modules/cop/drawing/map-drawing.service';
import { LocationWidget } from '../shared/location-widget';

@Component({
  selector: 'topo-location-cards-widget',
  imports: [TranslatePipe, LocationCardComponent, CdkDropList, CdkDrag],
  templateUrl: './location-cards-widget.component.html',
  styleUrl: './location-cards-widget.component.scss'
})
export class LocationCardsWidgetComponent extends LocationWidget implements OnInit, OnDestroy {
  private weatherApi = inject(WeatherApiService);
  protected weatherConditions = signal<Record<string, WeatherCondition>>({});
  private mapDrawing = inject(MapDrawingService);

  protected locations = computed(() => {
    const locations = this.state.locations();
    const { orderByLongitude } = this.state.locationCardSettings();
    return orderByLongitude
      ? [...locations].sort((a, b) => {
          if (a?.isWorld) {
            return -1;
          }

          if (b?.isWorld) {
            return 1;
          }

          return a?.focusPoint?.longitude - b?.focusPoint?.longitude;
        })
      : locations;
  });

  private animationTimer: NodeJS.Timeout;
  private weatherUpdateTimer: NodeJS.Timeout;

  protected cardDisplayCount = computed(() => {
    // * NOTE * - this logic was brought over from old component
    const totalLocations = this.locations()?.length ?? 0;

    if (totalLocations <= 2) {
      return 1;
    }

    if (totalLocations >= 7) {
      return 5;
    }

    const displayAmount = totalLocations - 2;

    if (displayAmount % 2 === 0) {
      return displayAmount - 1;
    }

    return displayAmount;
  });

  protected visibleCardsCenterIndex = computed(() => {
    const cardDisplayCount = this.cardDisplayCount();
    return Math.floor(cardDisplayCount / 2);
  });

  protected cardWidth = computed(() => {
    if (this.state.isMapInWorkbenchMode()) {
      return 'auto';
    }

    const divider = this.cardDisplayCount();
    return `${100 / divider}%`;
  });

  loadWeather$ = effect(() => {
    const settings = this.state.locationCardSettings();

    if (!Boolean(settings)) {
      return;
    }

    const { showTemperature, showWeather } = settings;

    if (showTemperature || showWeather) {
      this.getWeatherConditions();
    }
  });

  protected currentLocationIndex = signal(0);

  protected locationCards = computed(() => {
    const locations = this.locations();

    if (this.state.isMapInWorkbenchMode()) {
      return locations.map(location => ({
        location,
        style: {
          opacity: 1,
          zIndex: 1,
          left: 0
        }
      }));
    }

    const currentLocationIndex = this.currentLocationIndex();
    const cardDisplayCount = this.cardDisplayCount();

    const stepsFromCenter = cardDisplayCount - this.visibleCardsCenterIndex() - 1;

    const idxArr = Array.from({ length: locations.length }, (_, index) => index);

    const visibleIndexes = [currentLocationIndex];
    const len = idxArr.length;

    const offToLeftIndex: number = idxArr.at((currentLocationIndex - stepsFromCenter - 1) % len);

    for (let i = 0; i < stepsFromCenter; i++) {
      const op = i + 1;
      const prev = idxArr.at((currentLocationIndex - op) % len);
      const next = idxArr.at((currentLocationIndex + op) % len);

      visibleIndexes.unshift(prev);
      visibleIndexes.push(next);
    }

    const cardWidth = 100 / this.cardDisplayCount();

    const selectedUId = this.selectedLocationUId();

    return locations.map((location, index) => {
      const visibleIndex = visibleIndexes.findIndex(n => n === index);
      const isVisible = visibleIndex > -1;
      const isSelected = selectedUId === location.uId;

      return {
        location,
        style: {
          opacity: isSelected ? 1 : isVisible ? 0.85 : 0,
          zIndex: isSelected ? 2 : isVisible ? 1 : 0,
          left: isVisible ? cardWidth * visibleIndex : index === offToLeftIndex ? -1 * cardWidth : 100
        }
      };
    });
  });

  private locationRenderCounter = signal(0);
  private shouldRefreshMap = computed(() => {
    const count = this.locationRenderCounter();
    return count > 20;
  });

  private showLocation(pauseForMapLoading = true): void {
    if (pauseForMapLoading && this.state.isMapLayersLoading()) {
      return;
    }

    this.locationRenderCounter.update(count => count + 1);

    const settings = this.state.locationCardSettings();
    const locations = this.locations();

    // location
    const {
      uId,
      isWorld,
      focusPoint: { latitude, longitude, zoomLevel }
    } = locations[this.currentLocationIndex()];

    this.selectedLocationUId.set(uId);

    this.mapDrawing.setCenterAndZoom(latitude, longitude, zoomLevel);

    if (isWorld && settings.worldShowDayNight) {
      this.mapDrawing.showNightLayer();
    } else {
      this.mapDrawing.hideNightLayer();
    }
  }

  ngOnInit(): void {
    const isWorkbenchMode = this.state.isMapInWorkbenchMode();

    if (!isWorkbenchMode) {
      // show first location in list, ignore waiting on map loading
      this.showLocation(false);

      const { showTemperature, showWeather, transitionTimeSeconds = 15 } = this.state.locationCardSettings();

      // update location to next in list, timing based on configured settings
      this.animationTimer = setInterval(() => {
        const shouldRefreshMap = this.shouldRefreshMap();

        if (shouldRefreshMap) {
          this.state.refreshMap();
          this.locationRenderCounter.set(0);
          return;
        }

        this.currentLocationIndex.update(i => {
          const nextIndex = i + 1;
          return nextIndex >= this.locations().length ? 0 : nextIndex;
        });

        this.showLocation();
      }, transitionTimeSeconds * 1000);

      if (showTemperature || showWeather) {
        // get weather updates every 10 minutes while in presentation mode
        this.weatherUpdateTimer = setInterval(() => {
          this.getWeatherConditions();
        }, 600000);
      }
    }
  }

  ngOnDestroy(): void {
    if (Boolean(this.animationTimer)) {
      clearInterval(this.animationTimer);
      this.animationTimer = null;
    }

    if (Boolean(this.weatherUpdateTimer)) {
      clearInterval(this.weatherUpdateTimer);
      this.weatherUpdateTimer = null;
    }
  }

  async getWeatherConditions(): Promise<void> {
    const locations = this.state.locations();
    const locationNames = locations.reduce((acc, { alpha2CountryCode, place }) => {
      if (Boolean(alpha2CountryCode) && Boolean(place?.locality)) {
        acc.push(`${place.locality},${alpha2CountryCode}`);
      }

      return acc;
    }, []);

    if (!locationNames.length) {
      return;
    }

    const weatherConditions = await this.weatherApi
      .getWeatherConditions(locationNames)
      .toPromise()
      .then(res =>
        locations.reduce((acc, location) => {
          if (Boolean(location?.alpha2CountryCode) && Boolean(location?.place?.locality)) {
            acc[location.uId] = res.find(weather => weather.locationName === `${location.place.locality},${location.alpha2CountryCode}`);
          }
          return acc;
        }, {})
      )
      .catch(err => {
        console.error(err);
        return null;
      });

    this.weatherConditions.set(weatherConditions);

    if (this.scrollAfterWeatherLoad()) {
      this.scrollAfterWeatherLoad.set(false);

      const { orderByLongitude } = this.state.locationCardSettings() ?? {};
      const selectedId = this.selectedLocationUId();

      if (orderByLongitude && !!selectedId) {
        this.scrollToCard(selectedId);
      } else {
        this.scrollToEnd();
      }
    }
  }

  // used in edit mode
  setSelectedLocation(locationUId: string): void {
    this.selectedLocationUId.set(locationUId);
  }

  private cardContainer = viewChild<ElementRef<HTMLDivElement>>('cardContainer');
  private scrollAfterWeatherLoad = signal(false);

  async addLocation(): Promise<void> {
    const newLocation = await this.getCurrentMapPositionAsWidgetLocation();

    if (!Boolean(newLocation)) {
      return;
    }

    const configuration = this.state.configuration();
    const locations = [...configuration.locations, newLocation];

    this.selectedLocationUId.set(newLocation.uId);
    this.state.updateConfiguration({ ...configuration, locations });

    if (configuration.locationCardSettings?.orderByLongitude) {
      this.scrollToCard(newLocation.uId);
    } else {
      this.scrollToEnd();
    }

    const { locationCardSettings } = configuration;

    if (!!locationCardSettings) {
      const { showTemperature, showWeather } = locationCardSettings;
      this.scrollAfterWeatherLoad.set(showTemperature || showWeather);
    }
  }

  private scrollToEnd(): void {
    setTimeout(() => {
      const containerEl = this.cardContainer().nativeElement;
      const lastEl = containerEl.lastElementChild;
      lastEl.scrollIntoView({ behavior: 'smooth', inline: 'end' });
    });
  }

  private scrollToCard(id: string): void {
    if (!!id) {
      setTimeout(() => {
        const containerEl = this.cardContainer().nativeElement;
        const lastEl = containerEl.querySelector(`#location-card_${id}`);
        lastEl.scrollIntoView({ behavior: 'smooth', inline: 'center' });
      });
    }
  }

  async setLocationFromMap(locationUId: string): Promise<void> {
    const newLocation = await this.getCurrentMapPositionAsWidgetLocation();

    if (!Boolean(newLocation)) {
      return;
    }

    const configuration = this.state.configuration();
    const locations = [...configuration.locations].map(l =>
      l.uId === locationUId
        ? {
            ...newLocation,
            uId: l.uId
          }
        : l
    );

    this.weatherConditions.update(current => {
      if (Boolean(current)) {
        delete current[locationUId];
      }

      return current;
    });
    this.state.updateConfiguration({ ...configuration, locations });
  }

  removeLocation(locationUId: string): void {
    const configuration = this.state.configuration();
    const locations = [...configuration.locations].filter(l => l.uId !== locationUId);

    this.state.updateConfiguration({
      ...configuration,
      locations
    });
  }

  changeLocationName(locationUId: string, name: string): void {
    const configuration = this.state.configuration();
    const locations = [...configuration.locations].map(l => (l.uId === locationUId ? { ...l, name } : l));

    this.state.updateConfiguration({
      ...configuration,
      locations
    });
  }

  drop(event: CdkDragDrop<WidgetLocation[]>) {
    const configuration = this.state.configuration();
    const locations = [...configuration.locations];

    moveItemInArray(locations, event.previousIndex, event.currentIndex);

    this.state.updateConfiguration({
      ...configuration,
      locations
    });
  }
}
